// src/components/search/LoadMoreProductGrid.tsx
import { useState } from "react";
import type {
  Product,
  ProductListingPage,
} from "@decocms/apps-commerce/types";
import { clx } from "@decocms/blocks/sdk/clx";
import ProductCard from "~/components/product/ProductCard";
import { invoke } from "~/runtime";
import { useUrlRebased, type Layout } from "./SearchTitleAndResult";

// Both PLP loaders (`productListingPage.ts` legado e
// `productListingPageLean.ts` moderno) devolvem `fq`/`sort` no `pageInfo`
// de verdade em runtime (ver `src/loaders/daniela/productListingPage.ts` e
// o vendor da Intelligent Search), mas o tipo compartilhado `PageInfo` de
// `@decocms/apps-commerce/types` não declara esses campos — só o
// necessário pra repassar `fq`/`sort`/`count` (via `recordPerPage`, que já
// é do tipo base) de um clique de "Carregar mais" pro próximo, sem o que a
// busca perde o filtro/ordenação configurados no bloco CMS.
type PageInfoWithLegacyFilters = ProductListingPage["pageInfo"] & {
  fq?: string;
  sort?: string;
};

export interface LoadMoreProductGridProps {
  initialProducts: Product[];
  initialPageInfo: PageInfoWithLegacyFilters;
  url: string;
  cardLayout?: Layout;
  useLegacyLoader: boolean;
}

type Status = "idle" | "loading" | "error";

function LoadMoreProductGrid({
  initialProducts,
  initialPageInfo,
  url,
  cardLayout,
  useLegacyLoader,
}: LoadMoreProductGridProps) {
  const [products, setProducts] = useState(initialProducts);
  const [pageInfo, setPageInfo] = useState(initialPageInfo);
  const [status, setStatus] = useState<Status>("idle");

  const nextPageUrl = useUrlRebased(pageInfo?.nextPage, url);

  const loadMore = async () => {
    if (!nextPageUrl) return;

    setStatus("loading");
    try {
      const loaderKey = useLegacyLoader
        ? "productListingPage"
        : "productListingPageLean";
      const response = await invoke["site"].loaders.daniela[loaderKey]({
        pageHref: nextPageUrl,
        __pageUrl: nextPageUrl,
        fq: pageInfo?.fq,
        sort: pageInfo?.sort,
        count: pageInfo?.recordPerPage,
      }) as ProductListingPage | null;

      // Um array vazio (`[]`) é truthy em JS — `!response?.products` sozinho
      // não pega esse caso, então uma resposta "bem-sucedida" mas vazia
      // (ex.: sort/fq perdidos numa borda ainda não coberta) não caía em
      // erro: nada era anexado, o pageInfo era trocado e o status voltava
      // pra "idle" — o botão continuava lá mas parava de fazer qualquer
      // coisa visível a partir daquele clique, um sintoma que já confundiu
      // o diagnóstico de um bug real anterior nessa mesma feature.
      if (!response?.products?.length) {
        setStatus("error");
        return;
      }

      setProducts((current) => {
        const seen = new Set(current.map((p) => p.productID));
        return [
          ...current,
          ...response.products.filter((p) => !seen.has(p.productID)),
        ];
      });
      setPageInfo(response.pageInfo);
      setStatus("idle");
    } catch {
      setStatus("error");
    }
  };

  return (
    <>
      <div
        data-product-list
        className={clx(
          "grid items-center",
          "grid-cols-2 gap-x-1.5 gap-y-4.5",
          "sm:grid-cols-4",
          "w-full",
          "px-2.5",
          "lg:px-[50px]",
        )}
      >
        {products.map((product, index) => (
          <ProductCard
            key={`product-card-${product.productID}`}
            product={product}
            preload={index === 0}
            layout={cardLayout}
          />
        ))}
      </div>

      {nextPageUrl && (
        <div
          role="status"
          className="pt-2 sm:pt-10 w-full flex flex-col justify-center items-center gap-2 [&_section]:contents"
        >
          {status === "error" && (
            <span className="text-sm text-[#B3261E]">
              Não foi possível carregar mais produtos.
            </span>
          )}
          <a
            rel="next"
            href={nextPageUrl}
            aria-disabled={status === "loading"}
            className="btn border-[#1A1A1A] bg-white hover:bg-white hover:border-[#1A1A1A] w-[260px] min-h-[40px] max-h-[40px] rounded-none border-[2px]"
            onClick={(event) => {
              // Só intercepta o clique esquerdo simples — deixa o browser
              // seguir o `href` de verdade em Cmd/Ctrl/Shift/Alt+click ou
              // clique do meio, senão perde-se a possibilidade de abrir a
              // próxima página numa aba nova, que era todo o motivo de usar
              // um `<a href>` de verdade aqui em vez de um botão comum.
              if (
                event.metaKey || event.ctrlKey || event.shiftKey ||
                event.altKey || event.button !== 0
              ) {
                return;
              }
              event.preventDefault();
              if (status !== "loading") loadMore();
            }}
          >
            <span className="inline uppercase text-base text-[#1A1A1A] font-normal">
              {status === "loading"
                ? "Carregando..."
                : status === "error"
                ? "Tentar de novo"
                : "Carregar mais"}
            </span>
          </a>
        </div>
      )}
    </>
  );
}

export default LoadMoreProductGrid;
